import { useCallback, useEffect, useState } from 'react';
import sessionsApi from 'Api/axios/sessions-api';

const DEFAULT_LIMIT = 25;

/**
 * Owns the attendance list for a session. Exposes the granular operations the
 * UI needs: paginated refetch, add, update single, bulk save, remove.
 *
 * Pagination is server-driven: callers pass `{ page, limit, status }` to
 * `fetchAttendances`; the hook stores both the current page rows and the
 * pagination envelope so the parent can render page navigation.
 */
export default function useAttendanceApi({ sessionId, init = true } = {}) {
  const [attendances, setAttendances] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAttendances = useCallback(
    async ({ status, page = 1, limit = DEFAULT_LIMIT, search } = {}) => {
      if (!sessionId) return { data: [], error: null };
      setLoading(true);
      setError(null);
      const trimmed = (search || '').trim();
      const response = await sessionsApi.listAttendances(sessionId, {
        page,
        limit,
        status: status && status !== 'all' ? status : undefined,
        search: trimmed ? trimmed : undefined,
      });
      if (response.error) {
        setError(response.error);
        setAttendances([]);
        setPagination(null);
      } else {
        setAttendances(response.data || []);
        setPagination(response.pagination || null);
      }
      setLoading(false);
      return response;
    },
    [sessionId]
  );

  useEffect(() => {
    if (init && sessionId) fetchAttendances();
  }, [sessionId, init, fetchAttendances]);

  const addAttendee = useCallback(
    async userId => sessionsApi.addAttendee(sessionId, userId),
    [sessionId]
  );

  const addAttendeesBulk = useCallback(
    async userIds => sessionsApi.addAttendeesBulk(sessionId, userIds),
    [sessionId]
  );

  const updateAttendance = useCallback(
    async (userId, payload) => sessionsApi.updateAttendance(sessionId, userId, payload),
    [sessionId]
  );

  const bulkSave = useCallback(
    async payload => sessionsApi.bulkAttendance(sessionId, payload),
    [sessionId]
  );

  const removeAttendee = useCallback(
    async userId => sessionsApi.removeAttendee(sessionId, userId),
    [sessionId]
  );

  return {
    attendances,
    pagination,
    loading,
    error,
    fetchAttendances,
    addAttendee,
    addAttendeesBulk,
    updateAttendance,
    bulkSave,
    removeAttendee,
  };
}
